<template>

    <div id="app">

        <div class="wrap">

            <div class="wrap-body">

                <transition
                        name="fade"
                        v-on:after-enter="afterEnterHandler"
                        v-on:after-leave="afterLeaveHandler"
                >
					    <!-- Render dynamic page component if provided -->
						<component
							v-if="pageComponent"
							:is="pageComponent"
							v-bind="pageProps"
						></component>

						<!-- Fallback to router-view for SPA mode -->
						<router-view v-else :key="$route.fullPath"></router-view>
                </transition>

                <app-menu :menus="menusFromProps" :tags="tagsFromProps"></app-menu>

            </div>

            <div class="wrap-footer">

                <app-footer :menus="menusFromProps" :footer-data="footerDataFromProps"></app-footer>

            </div>

            <popup-accordion></popup-accordion>
            <popup-external-link></popup-external-link>
            <back-to-top></back-to-top>
        </div>

    </div>

</template>

<script>

    console.log('App');

    import env from './env';

    import wpApi from './services/wpApi';

    import AppFooter from './components/AppFooter.vue';
    import AppMenu from './components/AppMenu.vue';
    import PopupAccordion from './components/PopupAccordion.vue';
    import PopupExternalLink from './components/PopupExternalLink.vue';
    import BackToTop from './components/BackToTop.vue';

    const TAGS_MAX_ITEMS = 10;

    export default {
        name : 'app',
		props: {
			pageComponent: {
				type: Object,
				default: null
			},
			pageProps: {
				type: Object,
				default: () => ({})
			},
			menus: {
				type: Object,
				default: () => ({})
			},
			tags: {
				type: Array,
				default: () => []
			},
			footerData: {
				type: Object,
				default: () => ({})
			}
		},
        created(){
            window.addEventListener('scroll', this.scrollHandler, { passive : true });
            this.load();
        },
		computed: {
			menusFromProps() {
				return this.menus && Object.keys(this.menus).length > 0
					? this.menus
					: (this.$root && this.$root.cache ? this.$root.cache.menus : {});
			},
			tagsFromProps() {
				return this.tags && this.tags.length > 0
					? this.tags
					: [];
			},
			footerDataFromProps() {
				console.log('App.vue footerData prop:', this.footerData);
				return this.footerData && Object.keys(this.footerData).length > 0
					? this.footerData
					: {};
			}
		},
        destroyed(){
            window.removeEventListener('scroll', this.scrollHandler);
        },
        mounted(){
            this.scrollHandler();
        },
        components : {
            AppMenu,
            AppFooter,
            PopupAccordion,
            PopupExternalLink,
            BackToTop
        },
        data : function(){
            return {
                scrollOffset : 100,
            };
        },
        metaInfo(){
          return {
            htmlAttrs : {
                // lang : 'de',
                // prefix : 'og: http://ogp.me/ns#',
            },
            link : [
                {
                    rel : 'canonical',
                    href: this.getCanonicalUrl(),
                },
                // {
                //     rel : 'home',
                //     href : 'https://nochvielvor.de',
                // },
            ],
            meta : [
                // { charset: 'utf-8' },
                {
                    vmid : 'description',
                    name : 'description',
                    content : 'Das Portal für Menschen mit HIV',
                    template : (content)=>{
                        return content ? content : 'Das Portal für Menschen mit HIV';
                    },
                },
                {
                    vmid : 'og:description',
                    property : 'og:description',
                    content : 'Das Portal für Menschen mit HIV',
                    template : (content)=>{
                        return content ? content : 'Das Portal für Menschen mit HIV';
                    },
                },
                {
                    vmid : 'og:image',
                    property : 'og:image',
                    content : 'http://www.nochvielvor.de/wp/wp-content/uploads/2018/09/meta_og_image_1200_630_fuer-ein-voellig-normales-und-aktives-leben-mit-hiv.jpg',
                    template : (content)=>{
                        return content ? content : 'http://www.nochvielvor.de/wp/wp-content/uploads/2018/09/meta_og_image_1200_630_fuer-ein-voellig-normales-und-aktives-leben-mit-hiv.jpg';
                    },
                },
                {
                    vmid : 'og:image:alt',
                    property : 'og:image:alt',
                    content : 'Das Portal für Menschen mit HIV',
                    template : (content)=>{
                        return content ? content : 'Das Portal für Menschen mit HIV';
                    },
                },
                {
                    vmid : 'og:image:width',
                    property : 'og:image:width',
                    content : '1200',
                },
                {
                    vmid : 'og:image:height',
                    property : 'og:image:height',
                    content : '630',
                },
                {
                    vmid : 'og:language',
                    property : 'og:language',
                    content : 'de_DE',
                },
                {
                    vmid : 'og:title',
                    property : 'og:title',
                    content : 'Das Portal für Menschen mit HIV - #nochvielvor',
                    template : (content)=>{
                        return content ? `${content}` : 'Das Portal für Menschen mit HIV - #nochvielvor';
                    },
                },
                {
                    vmid : 'og:type',
                    property : 'og:type',
                    content : 'website',
                },
                {
                    vmid : 'og:url',
                    property : 'og:url',
                    content : 'http://www.nochvielvor.de',
                    template : (content)=>{
                        return content ? content : 'http://www.nochvielvor.de';
                    },
                },
            ],
            title : 'Das Portal für Menschen mit HIV',
            titleTemplate : (content)=>{

                // NOTE: As WordPress encodes & as HTML entity &#038; in titles it needs to be decoded here.

                return content ? `${wpApi.decodeHtmlEntityAmpersand(content)} - #nochvielvor` : 'Das Portal für Menschen mit HIV - #nochvielvor';
            },
          }
        },
        methods : {
            afterEnterHandler(){
                this.$root.events.$emit('route-transition');
            },
            afterLeaveHandler(){
                window.scrollTo(0, 0);
            },
            load(){
                // NO API CALLS - App.vue is legacy SPA root, not used in WordPress-rendered mode
                console.warn('App.vue: load() - All data must come from PHP, no API calls allowed');
                return;
                this.$root.promises.home = wpApi.http.get(wpApi.host + wpApi.baseUrl + '/wp-json/acf/v3/pages/' + env.PAGE_ID_STARTSEITE);
                this.$root.promises.menus = wpApi.http.get(wpApi.host + wpApi.baseUrl + '/wp-json/nochvielvor/v1/menus');
                this.$root.promises.menus
                    .then((response)=>{
                        console.log('response', response);
                        this.$set(this.$root.cache.menus, 'main-menu-primary', wpApi.getHierarchicalMenuItems(response.data['main-menu-primary']));
                        this.$set(this.$root.cache.menus, 'main-menu-secondary', wpApi.getHierarchicalMenuItems(response.data['main-menu-secondary']));
                        this.$set(this.$root.cache.menus, 'footer-menu-topics', wpApi.getHierarchicalMenuItems(response.data['footer-menu-topics']));
                        this.$set(this.$root.cache.menus, 'footer-menu-legal', wpApi.getHierarchicalMenuItems(response.data['footer-menu-legal']));
                    })
                    .catch((error)=>{
                        console.log('error', error);
                    });
                this.$root.promises.options = wpApi.http.get(wpApi.host + wpApi.baseUrl + '/wp-json/nochvielvor/v1/option/global-fields');
                this.$root.promises.options
                    .then((response)=>{
                        console.log('response', response);
                        this.$set(this.$root.cache.options, 'global-fields', response.data);
                    })
                    .catch((error)=>{
                        console.log('error', error);
                    });
                this.$root.promises.tags = wpApi.http.get(wpApi.host + wpApi.baseUrl + '/wp-json/wp/v2/primary_keyword?per_page=100');
                this.$root.promises.tags
                    .then((response)=>{
                        console.log('response', response);
                        if (Array.isArray(response.data) === false || response.data.length === 0) {
                            return;
                        }
                        response.data = response.data
                            .sort((a, b)=>{
                                if (a['count'] < b['count']) {
                                    return 1;
                                }
                                if (a['count'] > b['count']) {
                                    return -1;
                                }
                                return 0;
                            })
                            .slice(0, TAGS_MAX_ITEMS)
                            .sort((a, b)=>{
                                if (a['name'] < b['name']) {
                                    return -1;
                                }
                                if (a['name'] > b['name']) {
                                    return 1;
                                }
                                return 0;
                            })
                        ;
                        this.$set(this.$root.cache.tags, 'primary_keyword', response.data);
                    })
                    .catch((error)=>{
                        console.log('error', error);
                    });
            },
            scrollHandler(){
				const scrollY = window.scrollY || window.pageYOffset;
				this.$root.states.viewIsScrolled = scrollY > this.scrollOffset;
			},
            getCanonicalUrl() {
                // Generate the canonical URL dynamically, e.g., based on the current route
                return `https://www.nochvielvor.de${this.$route.fullPath}`;
            },
        },
        watch: {
            $route() {
                this.$meta().refresh();
            },
        },
    }

</script>

<style lang="scss">

    @import '../css/style';

    .fade-enter-active {
        transition: opacity 400ms 400ms;
    }

    .fade-leave-active {
        transition: opacity 400ms;
    }

    .fade-enter-to,
    .fade-leave {
        opacity: 1;
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }

</style>
